import { apiClient } from './client';
import type {
  CalendarEventDto,
  CalendarRenewalDto,
  CalendarReplacementRobotRef,
  EventRobotLineDto,
  CalendarReplacementDto,
  DeploymentKind,
  RenewalRobotPlanItem,
  DeploymentReturnReason,
  RobotReturnLocation,
  ServiceOrderSummaryDto,
} from '@bhops/shared';

// 통합 캘린더 끌어 옮기기(2026-09-30). 서버가 인프라 발주 저장과 같은 규칙으로 막고, 까닭을
// 400 으로 돌려준다. 옮긴 날짜는 1분 안에 구글 캘린더에도 간다.
//
// time(2026-10-19, 선택): 빼면 지금 시각 그대로(끌어 옮기기) · null = 하루 종일 · 'HH:mm' = 그 시각.
type Time = string | null | undefined;
const withTime = (body: Record<string, unknown>, time: Time) =>
  time === undefined ? body : { ...body, time };

export const scheduleDatesApi = {
  /** 그릴러 발주(order)·납품(delivery)·설치 예정(install) 날짜 하나. date = YYYY-MM-DD */
  async moveInfra(
    orderId: string,
    kind: 'order' | 'delivery' | 'install',
    date: string,
    time?: Time,
  ): Promise<{ ok: boolean; changed: boolean }> {
    const { data } = await apiClient.patch(
      `/schedule-dates/infrastructure-orders/${orderId}`,
      withTime({ kind, date }, time),
    );
    return data;
  },
  /**
   * 예방정비 방문 예정일 — 시각을 안 주면 날짜만 옮긴다. memo(2026-10-20 우클릭 '수정')는 캘린더에서
   * 잡은 유지보수의 메모 — 빼면 그대로.
   */
  async moveMaintenance(
    serviceOrderId: string,
    date: string,
    time?: Time,
    memo?: string,
  ): Promise<{ ok: boolean; changed: boolean }> {
    const { data } = await apiClient.patch(
      `/schedule-dates/service-orders/${serviceOrderId}`,
      withTime({ date, ...(memo !== undefined ? { memo } : {}) }, time),
    );
    return data;
  },
  // ── 우클릭 '삭제'(2026-10-20) ──
  /** 인프라 발주의 그 날짜(발주 · 납품 · 설치 예정)를 비운다. 발주는 남는다. */
  async clearInfra(
    orderId: string,
    kind: 'order' | 'delivery' | 'install',
  ): Promise<{ ok: boolean; changed: boolean }> {
    const { data } = await apiClient.delete(`/schedule-dates/infrastructure-orders/${orderId}`, {
      params: { kind },
    });
    return data;
  },
  /** 캘린더에서 잡은 유지보수는 오더 + 고장 조치 이력을 지우고, 자동 예방정비는 날짜만 비운다. */
  async deleteMaintenance(
    serviceOrderId: string,
  ): Promise<{ ok: boolean; changed: boolean; mode: 'deleted' | 'cleared' }> {
    const { data } = await apiClient.delete(`/schedule-dates/service-orders/${serviceOrderId}`);
    return data;
  },
  /** 회수 예정일을 비운다. */
  async clearReturn(customerId: string): Promise<{ ok: boolean; changed: boolean }> {
    const { data } = await apiClient.delete(`/schedule-dates/customers/${customerId}/return-date`);
    return data;
  },
  /**
   * 회수 예정일(고객사 단위) — 2026-10-19 통합 캘린더 '일정 추가' · 끌어 옮기기. date = YYYY-MM-DD
   */
  async moveReturn(
    customerId: string,
    date: string,
    time?: Time,
  ): Promise<{ ok: boolean; changed: boolean }> {
    const { data } = await apiClient.patch(
      `/schedule-dates/customers/${customerId}/return-date`,
      withTime({ date }, time),
    );
    return data;
  },
  /**
   * 캘린더에서 유지보수 잡기(2026-10-19) — 예방정비 서비스 오더 + 고객사 리포트(진행중, 2026-11-02 — 종전엔
   * 고장 조치 이력)를 서버가 함께 만든다. 리포트 번호는 방문일로 자동(P{YYMMDD}-NN). time 이 없으면 하루 종일.
   */
  async createMaintenance(body: {
    customerId: string;
    /** 2026-10-22 — 여러 대면 로봇마다 오더, 한 일정 · 리포트 하나. */
    robotIds: string[];
    date: string;
    time?: string | null;
    memo?: string;
    /** 2026-11-02 — 증상(선택). 고객사 리포트의 요약이 된다. 비우면 작업 메모로. */
    symptom?: string;
    /** 2026-11-03 — 기존 고객사 리포트에 잇기. 주면 새 리포트를 만들지 않는다. */
    linkReportId?: string;
  }): Promise<{
    serviceOrder: ServiceOrderSummaryDto;
    customerSheetReportId: string;
    reportNo: string;
    /** 기존 리포트에 이었나 */
    linked?: boolean;
  }> {
    const { data } = await apiClient.post('/schedule-dates/maintenance', body);
    return data;
  },

  // ── 로봇 교체(2026-10-20 — 설치 + 회수를 한 번에, 여러 대를 한 일정으로) ──
  // id 는 일정 id(묶음 번호 — 한 대 교체면 그 서비스 오더 id).
  /** 캘린더에 그릴 로봇 교체 일정('로봇 교체' 서비스 오더 묶음). */
  async listReplacements(): Promise<{ items: CalendarReplacementDto[] }> {
    const { data } = await apiClient.get('/schedule-dates/replacements');
    return data;
  },
  /** 교체 일정 추가 — 고객사 · 뺄 로봇들. 서버가 로봇마다 제조팀에 교체 요청 알람을 보낸다. */
  async createReplacement(body: {
    customerId: string;
    robotIds: string[];
    date: string;
    time?: string | null;
    memo?: string;
  }): Promise<{
    id: string;
    serviceOrders: Array<{ id: string; humanId: string }>;
    replacementAlarmSent: boolean;
  }> {
    const { data } = await apiClient.post('/schedule-dates/replacements', body);
    return data;
  },
  /** 교체 완료 — 고른 로봇마다 뺄 로봇 회수 + 넣을 로봇 배치(한 거래) + 오더 해결. 일부만 해도 된다. */
  async completeReplacement(
    id: string,
    body: {
      date: string;
      items: Array<{ id: string; newRobotId?: string }>;
      returnReason?: DeploymentReturnReason;
      returnLocation?: RobotReturnLocation;
      notes?: string;
    },
  ): Promise<{
    ok: boolean;
    closed: boolean;
    allDone: boolean;
    results: Array<{ id: string; ok: boolean; closed?: boolean; error?: string }>;
  }> {
    const { data } = await apiClient.post(`/schedule-dates/replacements/${id}/complete`, body);
    return data;
  },
  /** 우클릭 '수정' · 끌어 옮기기 — 다 마친 교체면 실제 교체일(회수일 · 배치일)도 같이 바뀐다. */
  async updateReplacement(
    id: string,
    body: { date?: string; time?: string | null; memo?: string },
  ): Promise<{ ok: boolean; changed: boolean; deploymentsMoved: number }> {
    const { data } = await apiClient.patch(`/schedule-dates/replacements/${id}`, body);
    return data;
  },
  /** 우클릭 '삭제' — 교체 오더를 취소한다. 마친 교체는 되돌린다(뺀 로봇이 다시 그 매장에). */
  async cancelReplacement(id: string): Promise<{ ok: boolean; changed: boolean; undone: number }> {
    const { data } = await apiClient.delete(`/schedule-dates/replacements/${id}`);
    return data;
  },

  // ── 재계약(2026-10-22 — 옛 고객사 → 새 고객사, 옛 계약 마감 + 새 계약 활성화) ──
  /** 캘린더에 그릴 재계약 일정. */
  async listRenewals(): Promise<{ items: CalendarRenewalDto[] }> {
    const { data } = await apiClient.get('/schedule-dates/renewals');
    return data;
  },
  /** 재계약 일정 추가 — 옛 · 새 고객사와 옛 고객사 로봇마다 회수 · 이어서 사용. */
  async createRenewal(body: {
    fromCustomerId: string;
    toCustomerId: string;
    date: string;
    time?: string | null;
    memo?: string;
    plan?: RenewalRobotPlanItem[];
  }): Promise<{ id: string }> {
    const { data } = await apiClient.post('/schedule-dates/renewals', body);
    return data;
  },
  /** 우클릭 '수정' · 끌어 옮기기. 마친 재계약은 일정만 바뀐다(실제 회수일 · 배치일은 그대로). */
  async updateRenewal(
    id: string,
    body: { date?: string; time?: string | null; memo?: string; plan?: RenewalRobotPlanItem[] },
  ): Promise<{ ok: boolean; changed: boolean }> {
    const { data } = await apiClient.patch(`/schedule-dates/renewals/${id}`, body);
    return data;
  },
  /** [재계약 완료] — 옛 로봇 회수 · 이어서 사용, 옛 계약 해지, 새 계약 로봇 설치 완료. */
  async completeRenewal(
    id: string,
    body: {
      date: string;
      time?: string | null;
      plan?: RenewalRobotPlanItem[];
      returnLocation?: RobotReturnLocation;
    },
  ): Promise<{
    ok: boolean;
    returned: string[];
    carried: string[];
    installed: string[];
    terminatedContracts: number;
    maintenanceOrders: string[];
  }> {
    const { data } = await apiClient.post(`/schedule-dates/renewals/${id}/complete`, body);
    return data;
  },
  /** 마친 재계약 되돌리기 — 로봇이 원래 계약으로, 해지한 옛 계약은 다시 살린다. */
  async undoRenewal(id: string): Promise<{
    ok: boolean;
    restoredRobots: number;
    uninstalled: number;
    cancelledMaintenanceOrders: string[];
    keptMaintenanceOrders: string[];
  }> {
    const { data } = await apiClient.post(`/schedule-dates/renewals/${id}/undo`, {});
    return data;
  },
  /** 우클릭 '삭제' — 일정만 지운다(마친 재계약도 로봇 · 계약은 그대로). */
  async deleteRenewal(id: string): Promise<{ ok: boolean; changed: boolean }> {
    const { data } = await apiClient.delete(`/schedule-dates/renewals/${id}`);
    return data;
  },

  // ── 이벤트(2026-11-02 — 행사장 설치 · 회수, 로봇은 로봇 배정 화면에서 배정) ──
  /** 캘린더에 그릴 이벤트 일정. */
  async listEvents(): Promise<{ items: CalendarEventDto[] }> {
    const { data } = await apiClient.get('/schedule-dates/events');
    return data;
  },
  async createEvent(body: CalendarEventInput): Promise<{ id: string }> {
    const { data } = await apiClient.post('/schedule-dates/events', body);
    return data;
  },
  /** 고치기 · 끌어 옮기기 — 빼면 그대로. */
  async updateEvent(
    id: string,
    body: Partial<CalendarEventInput>,
  ): Promise<{ ok: boolean; changed: boolean }> {
    const { data } = await apiClient.patch(`/schedule-dates/events/${id}`, body);
    return data;
  },
  /** 우클릭 '삭제' — 설치 · 회수 일정을 함께 지우고 배정한 로봇을 푼다. */
  async deleteEvent(id: string): Promise<{ ok: boolean; released: number }> {
    const { data } = await apiClient.delete(`/schedule-dates/events/${id}`);
    return data;
  },
  /** [설치 완료] — 배정된 로봇마다 출고 기록(로봇 배치의 행사 · 전시와 같은 모양)을 만든다. */
  async installEvent(id: string): Promise<{ ok: boolean; deployed: number }> {
    const { data } = await apiClient.post(`/schedule-dates/events/${id}/install`, {});
    return data;
  },
  /** [설치 완료] 되돌리기 — 출고를 없던 일로(배정은 남는다). */
  async undoInstallEvent(id: string): Promise<{ ok: boolean; undone: number }> {
    const { data } = await apiClient.post(`/schedule-dates/events/${id}/undo-install`, {});
    return data;
  },
  /** [회수 완료] — 출고를 반납 처리하고 로봇을 정비로, 배정은 푼다. */
  async returnEvent(id: string): Promise<{ ok: boolean; released: number }> {
    const { data } = await apiClient.post(`/schedule-dates/events/${id}/return`, {});
    return data;
  },
  async undoReturnEvent(id: string): Promise<{ ok: boolean }> {
    const { data } = await apiClient.post(`/schedule-dates/events/${id}/undo-return`, {});
    return data;
  },
  /** 로봇 배정 화면 — 이 이벤트에 배정할 수 있는 재고 로봇. */
  async eventCandidates(
    id: string,
  ): Promise<{ items: Array<CalendarReplacementRobotRef & { modelId: string; status: string }> }> {
    const { data } = await apiClient.get(`/schedule-dates/events/${id}/candidates`);
    return data;
  },
  /** 설치 완료한 이벤트면 배정과 함께 출고로 남는다(deployed). */
  async assignEventRobots(
    id: string,
    robotIds: string[],
  ): Promise<{ ok: boolean; assigned?: number; deployed?: number }> {
    const { data } = await apiClient.post(`/schedule-dates/events/${id}/robots`, { robotIds });
    return data;
  },
  /** 이미 나간 로봇이면 그 출고도 되돌린다(undeployed). */
  async releaseEventRobot(
    id: string,
    robotId: string,
  ): Promise<{ ok: boolean; undeployed?: boolean }> {
    const { data } = await apiClient.delete(`/schedule-dates/events/${id}/robots/${robotId}`);
    return data;
  },
};

/** 이벤트 일정 추가 · 고치기의 몸통. */
export interface CalendarEventInput {
  location: string;
  content: string;
  /** 이벤트 유형(CALENDAR_EVENT_KINDS) — 출고 기록의 유형이 된다. */
  kind: DeploymentKind;
  note: string | null;
  mapUrl: string | null;
  installDate: string;
  installTime: string | null;
  returnDate: string;
  returnTime: string | null;
  checklist: string[];
  robotLines: EventRobotLineDto[];
}
